import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import {
  Badge,
  Button,
  Card,
  Drawer,
  Field,
  Input,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Table,
  type Tone,
} from '../../design';
import { useToast } from '../../design/Toast';
import { fmtRelative } from '../../lib/format';
import { queryParams, useUrlState } from '../../lib/urlState';

type User = components['schemas']['User'];
type UserInvite = components['schemas']['UserInvite'];
type UserUpdate = components['schemas']['UserUpdate'];
type Role = components['schemas']['Role'];
type Org = components['schemas']['Organization'];

const STATUS: Record<User['status'], { label: string; tone: Tone }> = {
  invited: { label: 'Invitat', tone: 'orange' },
  active: { label: 'Activ', tone: 'green' },
  disabled: { label: 'Dezactivat', tone: 'neutral' },
};

export function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => unwrap(api.GET('/roles')) });
}

export function useOrgOptions() {
  return useQuery({
    queryKey: ['orgs', 'options'],
    queryFn: () => unwrap(api.GET('/orgs', { params: { query: { pageSize: 100 } } })),
  });
}

export function UsersPage() {
  const { user: me } = useAuth();
  const { values, page, pageSize, set } = useUrlState(['q', 'status', 'orgId'] as const);
  const [inviting, setInviting] = useState(false);
  const [selected, setSelected] = useState<User | null>(null);
  const roles = useRoles();
  const orgs = useOrgOptions();
  const orgName = (id: string | null) =>
    id === null ? 'Global' : (orgs.data?.items.find((o: Org) => o.id === id)?.name ?? '…');

  const query = useQuery({
    queryKey: ['users', { ...values, page, pageSize }],
    queryFn: () =>
      unwrap(
        api.GET('/users', {
          params: {
            query: queryParams({
              q: values.q,
              status: (values.status || undefined) as User['status'] | undefined,
              orgId: values.orgId || undefined,
              page,
              pageSize,
            }),
          },
        }),
      ),
  });

  return (
    <>
      <PageHeader
        title="Utilizatori & 2FA"
        subtitle="Invitare, roluri, perimetru și autentificarea în doi pași, obligatorie pentru toți."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setInviting(true)}>
            Invită utilizator
          </Button>
        }
      />
      <Card tight>
        <div className="flex wrap" style={{ padding: 16 }}>
          <Input
            placeholder="Caută după nume sau e-mail…"
            value={values.q}
            onChange={(e) => set({ q: e.target.value })}
            style={{ maxWidth: 300 }}
            aria-label="Căutare"
          />
          <Select
            value={values.status}
            onChange={(e) => set({ status: e.target.value })}
            style={{ maxWidth: 180 }}
            aria-label="Stare"
          >
            <option value="">Toate stările</option>
            <option value="invited">Invitați</option>
            <option value="active">Activi</option>
            <option value="disabled">Dezactivați</option>
          </Select>
          {me?.orgId === null && (
            <Select
              value={values.orgId}
              onChange={(e) => set({ orgId: e.target.value })}
              style={{ maxWidth: 240 }}
              aria-label="Organizație"
            >
              <option value="">Toate organizațiile</option>
              {orgs.data?.items.map((o: Org) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          )}
        </div>
        <Table<User>
          columns={[
            {
              key: 'name',
              header: 'Utilizator',
              render: (u) => (
                <div>
                  <strong>{u.name}</strong>
                  <div className="small muted">{u.email}</div>
                </div>
              ),
            },
            { key: 'role', header: 'Rol', render: (u) => u.role.name },
            { key: 'org', header: 'Perimetru', render: (u) => orgName(u.orgId) },
            {
              key: 'status',
              header: 'Stare',
              render: (u) => <Badge tone={STATUS[u.status].tone}>{STATUS[u.status].label}</Badge>,
            },
            {
              key: 'totp',
              header: '2FA',
              render: (u) =>
                u.totpEnabled ? (
                  <Badge tone="green">activ</Badge>
                ) : (
                  <Badge tone="orange">neconfigurat</Badge>
                ),
            },
            {
              key: 'last',
              header: 'Ultima autentificare',
              render: (u) => <span className="muted">{fmtRelative(u.lastLoginAt)}</span>,
            },
          ]}
          rows={query.data?.items ?? []}
          rowKey={(u) => u.id}
          onRowClick={setSelected}
          selectedKey={selected?.id ?? null}
          empty={
            query.isLoading
              ? 'Se încarcă…'
              : query.error
                ? errorMessage(query.error)
                : 'Niciun utilizator.'
          }
          footer={
            query.data && (
              <Pagination
                page={query.data.page}
                pageSize={query.data.pageSize}
                total={query.data.total}
                onPage={(p) => set({ page: p })}
              />
            )
          }
        />
      </Card>
      {inviting && (
        <InviteDialog
          roles={roles.data ?? []}
          orgs={orgs.data?.items ?? []}
          onClose={() => setInviting(false)}
        />
      )}
      {selected && (
        <UserDrawer
          user={selected}
          roles={roles.data ?? []}
          orgName={orgName(selected.orgId)}
          onClose={() => setSelected(null)}
          onChanged={setSelected}
        />
      )}
    </>
  );
}

function InviteDialog({
  roles,
  orgs,
  onClose,
}: {
  roles: Role[];
  orgs: Org[];
  onClose: () => void;
}) {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const form = useForm<UserInvite>({
    defaultValues: { name: '', email: '', roleId: '', orgId: me?.orgId ?? null },
  });
  const invite = useMutation({
    mutationFn: (d: UserInvite) =>
      unwrap(
        api.POST('/users', { body: { ...d, orgId: d.orgId === ('' as unknown) ? null : d.orgId } }),
      ),
    onSuccess: (u) => {
      toast(`Invitația pentru ${u.email} a fost trimisă.`, 'success');
      void qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const errors = form.formState.errors;
  const global = me?.orgId === null;
  return (
    <Modal
      title="Invită un utilizator"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          <Button
            variant="primary"
            onClick={form.handleSubmit((d) => invite.mutate(d))}
            busy={invite.isPending}
          >
            Trimite invitația
          </Button>
        </>
      }
    >
      <form className="stack" onSubmit={form.handleSubmit((d) => invite.mutate(d))}>
        <Field label="Nume" error={errors.name?.message} htmlFor="inv-name">
          <Input
            id="inv-name"
            autoFocus
            invalid={!!errors.name}
            {...form.register('name', {
              required: 'Numele este obligatoriu',
              minLength: { value: 2, message: 'Minimum 2 caractere' },
            })}
          />
        </Field>
        <Field label="E-mail" error={errors.email?.message} htmlFor="inv-email">
          <Input
            id="inv-email"
            type="email"
            invalid={!!errors.email}
            {...form.register('email', { required: 'E-mailul este obligatoriu' })}
          />
        </Field>
        <Field label="Rol" error={errors.roleId?.message} htmlFor="inv-role">
          <Select
            id="inv-role"
            invalid={!!errors.roleId}
            {...form.register('roleId', { required: 'Alege un rol' })}
          >
            <option value="">Alege…</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.builtin ? '' : ' (personalizat)'}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Perimetru"
          hint={
            global
              ? 'Global = acces la toate organizațiile (doar rolurile globale).'
              : 'Utilizatorii invitați rămân în organizația ta.'
          }
          htmlFor="inv-org"
        >
          <Select id="inv-org" disabled={!global} {...form.register('orgId')}>
            {global && <option value="">Global (toate organizațiile)</option>}
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <p className="small muted">
          Utilizatorul primește un link de activare, își alege parola și configurează aplicația de
          autentificare (TOTP).
        </p>
      </form>
    </Modal>
  );
}

function UserDrawer({
  user,
  roles,
  orgName,
  onClose,
  onChanged,
}: {
  user: User;
  roles: Role[];
  orgName: string;
  onClose: () => void;
  onChanged: (u: User) => void;
}) {
  const { user: me, withStepUp } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const form = useForm<UserUpdate>({
    defaultValues: {
      name: user.name,
      roleId: user.role.id,
      status: user.status === 'disabled' ? 'disabled' : 'active',
    },
  });
  const done = (msg: string) => (u: User) => {
    toast(msg, 'success');
    onChanged(u);
    void qc.invalidateQueries({ queryKey: ['users'] });
  };
  const update = useMutation({
    mutationFn: (d: UserUpdate) =>
      unwrap(api.PATCH('/users/{id}', { params: { path: { id: user.id } }, body: d })),
    onSuccess: done('Utilizatorul a fost actualizat.'),
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const reset2fa = useMutation({
    mutationFn: () =>
      withStepUp((headers) =>
        unwrap(
          api.POST('/users/{id}/reset-2fa', { params: { path: { id: user.id }, header: headers } }),
        ),
      ),
    onSuccess: () => {
      toast(
        '2FA a fost resetat; utilizatorul primește o nouă invitație de configurare.',
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const resend = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/users/{id}/resend-invitation', { params: { path: { id: user.id } } })),
    onSuccess: () => toast('Invitația a fost retrimisă.', 'success'),
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const isMe = me?.id === user.id;
  const invited = user.status === 'invited';
  return (
    <Drawer
      title={user.name}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Închide</Button>
          <Button
            variant="primary"
            onClick={form.handleSubmit((d) => update.mutate(d))}
            busy={update.isPending}
            disabled={invited}
          >
            Salvează
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="flex wrap">
          <Badge tone={STATUS[user.status].tone}>{STATUS[user.status].label}</Badge>
          <span className="muted small">{user.email}</span>
          <span className="muted small">· {orgName}</span>
        </div>
        <Field label="Nume" htmlFor="u-name">
          <Input id="u-name" disabled={invited} {...form.register('name', { minLength: 2 })} />
        </Field>
        <Field label="Rol" htmlFor="u-role">
          <Select id="u-role" disabled={invited || isMe} {...form.register('roleId')}>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Stare"
          hint={isMe ? 'Nu îți poți dezactiva propriul cont.' : undefined}
          htmlFor="u-status"
        >
          <Select id="u-status" disabled={invited || isMe} {...form.register('status')}>
            <option value="active">Activ</option>
            <option value="disabled">Dezactivat</option>
          </Select>
        </Field>
        <div className="sep" />
        <h3>Autentificare în doi pași</h3>
        {invited ? (
          <>
            <p className="muted small">Utilizatorul nu a acceptat încă invitația.</p>
            <Button icon="mail" onClick={() => resend.mutate()} busy={resend.isPending}>
              Retrimite invitația
            </Button>
          </>
        ) : (
          <>
            <p className="muted small">
              Resetarea șterge secretul TOTP și trimite o nouă invitație de configurare. Cere
              confirmare suplimentară.
            </p>
            <Button
              variant="danger"
              icon="refresh"
              onClick={() => reset2fa.mutate()}
              busy={reset2fa.isPending}
              disabled={isMe}
            >
              Resetează 2FA
            </Button>
          </>
        )}
      </div>
    </Drawer>
  );
}
